import { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import style from './SaveButton.module.scss';

import { Button, ButtonModifiers, ButtonTheme } from '@components/Button';
import { CommonTestAttributes } from 'chaika-test-attributes';
import { translate } from '@chaika/i18n/helper';

interface SaveButtonProps {
    withSaveAnimation?: boolean;
    modifiers?: ButtonModifiers[];
    theme?: ButtonTheme;
    disabled?: boolean;
    label?: string;
    dataTest?: string;
    onSaveClick(): void | Promise<void>;
}

const SAVE_DURATION = 5 * 1000;

export function SaveButton({
    withSaveAnimation,
    disabled,
    label,
    theme,
    modifiers,
    dataTest = CommonTestAttributes.SaveButton,
    onSaveClick,
}: SaveButtonProps) {
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        let timeOutId: NodeJS.Timeout;

        if (isSaving) {
            timeOutId = setTimeout(() => {
                setIsSaving(false);
            }, SAVE_DURATION);
        }

        return () => {
            if (timeOutId) {
                clearTimeout(timeOutId);
            }
        };
    }, [isSaving]);

    const onSave = useCallback(() => {
        if (withSaveAnimation) {
            setIsSaving(true);
        }

        onSaveClick();
    }, [withSaveAnimation, onSaveClick]);

    return (
        <>
            <Button
                disabled={disabled || isSaving}
                theme={theme ?? ButtonTheme.Main}
                modifiers={modifiers}
                dataTest={dataTest}
                onClick={onSave}
            >
                {label ? label : translate('save')}
            </Button>
            {withSaveAnimation && isSaving && (
                <div
                    className={classNames([style.saved])}
                    style={{ ['--save-duration']: `${SAVE_DURATION}ms` }}
                >
                    {translate('saved')}
                </div>
            )}
        </>
    );
}
